import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { GUIDE_METADATA } from "@/data/guide-slugs"
import { CARE_TASK_PILLARS } from "@/data/care-task-pillars"
import { CareTaskLandingCard } from "@/components/guides/care-task-landing-card"

const CARE_TASK_ORDER = ["fertilizing", "watering", "mowing", "weed-control"] as const
const CARE_TASK_SHORT_DESCRIPTIONS: Record<string, string> = {
  fertilizing: "When to feed, how much to apply, and the right NPK ratio for your grass.",
  watering: "How much water your lawn needs and how soil type changes the rules.",
  mowing: "Right cutting height, how often to mow, and the one-third rule.",
  "weed-control": "Pre-emergent timing, post-emergent strategy, and weed-by-weed ID.",
}

interface PillarCrossLinksProps {
  /** When provided, omit the row matching this slug from each section. */
  currentSlug?: string
  /** Visual variant. "compact" suits sidebars; "wide" suits in-body sections. */
  variant?: "compact" | "wide"
}

interface CareTaskLinksForGrassProps {
  /** Grass-key for analytics labeling only. Not used for filtering since all
   *  four care-task pillars apply to every grass. */
  grassKey?: string
}

/**
 * Renders the four care-task pillars as a 2x2 / 4-up grid using the same
 * landing-card UI as the /guides hub. Used on grass-type guides to surface
 * the cross-pillar links Google's 2026-05-03 interlink campaign added.
 */
export function CareTaskLinksForGrass(_props: CareTaskLinksForGrassProps) {
  const pillars = CARE_TASK_ORDER.map((slug) => CARE_TASK_PILLARS[slug]).filter(
    (p): p is NonNullable<typeof p> => Boolean(p)
  )
  return (
    <div data-module="cross_link" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-5">
      {pillars.map((pillar) => (
        <CareTaskLandingCard
          key={pillar.slug}
          pillar={pillar}
          shortDescription={CARE_TASK_SHORT_DESCRIPTIONS[pillar.slug] ?? pillar.heroSubtitle}
        />
      ))}
    </div>
  )
}

/**
 * The cluster cross-link matrix. Renders both the grass-type and care-task
 * pillar lists so any guide page can drop one block into its body and pick up
 * topical-cluster reinforcement automatically.
 */
export function PillarCrossLinks({ currentSlug, variant = "wide" }: PillarCrossLinksProps) {
  const careTasks = Object.values(CARE_TASK_PILLARS).filter(
    (p) => p.slug !== currentSlug
  )
  const grasses = GUIDE_METADATA.filter((g) => g.slug !== currentSlug)

  const itemBase =
    variant === "compact"
      ? "text-sm text-gray-700 hover:text-green-700"
      : "rounded-2xl border border-white/55 bg-white/55 backdrop-blur-md px-4 py-3 text-sm text-gray-800 hover:border-white/85 hover:bg-white/75 hover:text-green-700 transition-colors"

  return (
    <div data-module="cross_link" className="grid gap-8 md:grid-cols-2">
      <section>
        <h3 className="m-0 mb-3 text-base font-bold text-gray-900">Care tasks</h3>
        <ul
          className={
            variant === "compact"
              ? "flex flex-col gap-2"
              : "grid grid-cols-1 gap-2 sm:grid-cols-2"
          }
        >
          {careTasks.map((p) => (
            <li key={p.slug}>
              <Link href={`/guides/${p.slug}`} className={`${itemBase} flex items-center gap-2`}>
                <span className="capitalize">{p.slug.replace(/-/g, " ")}</span>
                <ArrowRight className="ml-auto h-3.5 w-3.5" strokeWidth={2.2} />
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <section>
        <h3 className="m-0 mb-3 text-base font-bold text-gray-900">Grass types</h3>
        <ul
          className={
            variant === "compact"
              ? "flex flex-col gap-2"
              : "grid grid-cols-1 gap-2 sm:grid-cols-2"
          }
        >
          {grasses.map((g) => (
            <li key={g.slug}>
              <Link href={`/guides/${g.slug}`} className={`${itemBase} flex items-center gap-2`}>
                <span>{g.displayName}</span>
                <ArrowRight className="ml-auto h-3.5 w-3.5" strokeWidth={2.2} />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
